import { useMemo, useState } from 'react';

import { computeDaysOpen, EntityRefUtil, IssueDTO, IssueSeverity, IssueSource, IssueStatus } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import notificationsErrorVector from '@/assets/vectors/notifications-error.svg';
import ArrowLeftIcon from '@/components/icons/arrow-left';
import ArrowRightIcon from '@/components/icons/arrow-right';
import { EllipsisHorizontalIcon } from '@/components/icons/ellipsis-horizontal';
import PlusIcon from '@/components/icons/plus';
import WarningIcon from '@/components/icons/warning';
import { WarningTriangleIcon } from '@/components/icons/warning-triangle';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { getAWAIssuesQueryOptions } from '@/queries/issues';
import { getIssueSeverityColorClass } from '@/utils/issue';

// Sort issues by severity (Critical > High > Medium > Low)
const severityOrder = {
  [IssueSeverity.Critical]: 0,
  [IssueSeverity.High]: 1,
  [IssueSeverity.Medium]: 2,
  [IssueSeverity.Low]: 3,
};

export function AWAIssuesAssetPage() {
  const { workspaceId, assetId } = useParams();
  const navigate = useNavigate();
  const [selectedIssue, setSelectedIssue] = useState<IssueDTO | null>(null);
  const [isIssueDrawerOpen, setIsIssueDrawerOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateIssueOpen, setIsCreateIssueOpen] = useState(false);
  const [viewMode, setViewMode] = useState<IssueStatus>(IssueStatus.Open);
  const { data: assetData, isLoading: assetLoading } = useQuery(getAssetByIdQueryOptions({ assetId, isAWA: true }));

  const { data: issuesData = [], isLoading: issuesLoading } = useQuery(
    getAWAIssuesQueryOptions({ assetId: assetId!, status: [viewMode], orderBy: { field: 'severity', direction: 'desc' } }),
  );

  const displayedIssues = useMemo(
    () => [...issuesData].sort((a, b) => (severityOrder[a.severity as IssueSeverity] ?? 4) - (severityOrder[b.severity as IssueSeverity] ?? 4)),
    [issuesData],
  );

  const isLoading = assetLoading || issuesLoading;

  const headerTitle =
    viewMode === IssueStatus.Closed
      ? 'Closed Issues'
      : assetData
        ? EntityRefUtil.createAssetLabel(assetData)
        : assetLoading
          ? 'Loading asset details...'
          : 'Issues';

  const handleBackClick = () => {
    if (viewMode === IssueStatus.Closed) {
      setViewMode(IssueStatus.Open);
    } else {
      navigate(`/${workspaceId}/issues`);
    }
  };

  return (
    <div className="flex min-h-[100dvh] w-full flex-col gap-2">
      <div className="border-b-grey-3 flex items-center justify-between border-b-2 bg-white px-4 py-4">
        <div className="flex items-center gap-1">
          <button aria-label="Go back" onClick={handleBackClick}>
            <ArrowLeftIcon size={30} />
          </button>
          <div className="line-clamp-1 text-lg font-medium">{headerTitle}</div>
        </div>
        {viewMode === IssueStatus.Open && (
          <Button className="p-1" type="button" variant="ghost" onClick={() => setIsMenuOpen(true)}>
            <EllipsisHorizontalIcon />
          </Button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3 px-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[100px] w-full" />
          ))}
        </div>
      )}

      {/* No Issues view */}
      {!isLoading && displayedIssues.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <img src={notificationsErrorVector} alt="No issues" className="h-[150px] w-[150px]" />
          <div className="text-lg font-medium">{viewMode === IssueStatus.Open ? 'No open issues' : 'No closed issues'}</div>
          <div className="text-secondary-text px-8 text-center text-sm">
            {viewMode === IssueStatus.Open ? 'No open issues found for this asset' : 'Closed issues will appear here once resolved'}
          </div>
        </div>
      )}

      {/* Issues list view */}
      {!isLoading && displayedIssues.length > 0 && (
        <div className="space-y-3 px-2">
          {displayedIssues.map((issue: IssueDTO) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              onClick={() => {
                setSelectedIssue(issue);
                setIsIssueDrawerOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* FAB (+) button for creating new issue */}
      {viewMode === IssueStatus.Open && (
        <div className="fixed bottom-4 z-50 mb-[65px] h-0 w-full max-w-[414px] pr-4 sm:max-w-[800px]">
          <Button
            variant="default"
            size="icon"
            className="bg-default-green hover:bg-default-dark-green float-right h-[65px] w-[65px] rounded-full"
            onClick={() => setIsCreateIssueOpen(true)}
            aria-label="Create issue">
            <PlusIcon size={30} />
          </Button>
        </div>
      )}

      {/* View issue drawer */}
      <IssueDrawer
        noTrigger
        asset={assetData ?? undefined}
        issueId={selectedIssue?.id}
        source={IssueSource.Manual}
        open={isIssueDrawerOpen}
        onOpenChange={setIsIssueDrawerOpen}
      />

      {/* Create issue drawer */}
      <IssueDrawer
        noTrigger
        asset={assetData ?? undefined}
        source={IssueSource.Manual}
        open={isCreateIssueOpen}
        onOpenChange={setIsCreateIssueOpen}
        callback={(newIssue) => {
          setSelectedIssue(newIssue);
          setIsIssueDrawerOpen(true);
        }}
      />

      {/* Bottom menu to view closed issues */}
      <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <SheetContent side="bottom" hideCloseIcon className="rounded-t-xl pb-[30px]">
          <div className="bg-grey-4 mx-auto mt-[8px] mb-[18px] h-1 w-10 rounded-full" />
          <Button
            type="button"
            variant="ghost"
            className="mx-4 flex w-full items-center justify-start gap-2 rounded-lg text-left"
            onClick={() => {
              setViewMode(IssueStatus.Closed);
              setIsMenuOpen(false);
            }}>
            <WarningTriangleIcon size={30} className="py-1" />
            <span className="text-sm">View closed issues</span>
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function IssueCard({ issue, onClick }: { issue: IssueDTO; onClick?: () => void }) {
  const isClosed = issue.status === IssueStatus.Closed;

  const daysOpen = computeDaysOpen({ createdAt: issue.createdAt, closedAt: issue.closedAt, status: issue.status });

  const creatorName = EntityRefUtil.createUserFullNameRef(issue.user, 'Unknown');

  return (
    <button type="button" className="flex w-full cursor-pointer flex-row items-start gap-2 rounded-lg border bg-white p-2 text-left" onClick={onClick}>
      {/* Left content */}
      <div className="flex flex-1 flex-col gap-1">
        {/* Row 1: Severity icon + Title + Status badge */}
        <div className="flex items-start justify-between gap-1">
          <div className="flex items-start gap-1">
            <SeverityIndicator severity={issue.severity} />
            <span className="text-sm font-medium text-black">{issue.title || issue.description || 'Issue'}</span>
          </div>
          <Badge variant={isClosed ? 'success' : 'grey'} size="sm">
            {isClosed ? 'Closed' : 'Open'}
          </Badge>
        </div>

        {/* Row 2: Created By */}
        <div className="text-secondary-text text-sm">Created By: {creatorName}</div>

        {/* Row 3: Days Open with badge */}
        <div className="flex items-start gap-1">
          <span className="text-secondary-text text-sm">Days Open:</span>
          <div className="bg-default-red flex h-5 w-5 items-center justify-center rounded-full">
            <span className="text-sm font-medium text-white">{daysOpen}</span>
          </div>
        </div>
      </div>

      {/* Right arrow */}
      <div className="flex items-center self-stretch">
        <ArrowRightIcon size={20} />
      </div>
    </button>
  );
}

function SeverityIndicator({ severity }: { severity: IssueSeverity | null }) {
  // Critical severity shows filled warning icon (same as GBW)
  if (severity === IssueSeverity.Critical) {
    return <WarningIcon size={20} className="text-default-red" />;
  }

  // Other severities show colored circle with border (matches GBW dropdown)
  const issueSeverityColorClass = severity ? getIssueSeverityColorClass(severity) : 'bg-grey-1 border-grey-5';

  return (
    <div className="grid size-5 place-items-center">
      <div className={cn('h-[15px] w-[15px] rounded-full border-[3px]', issueSeverityColorClass)} />
    </div>
  );
}
